/* eslint-disable import/prefer-default-export */

'use client';

import { ColumnDef, Row } from '@tanstack/react-table';
import { Eye, MoreHorizontal, PenLine, Trash2 } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../../components/ui/button';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { IPartner } from '../../types/types';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useDialog } from '../../hooks/useDialog';
import EditFormPartner from './Edit/EditFormPartner';
import usePartner from '../../stores/usePartner';

function DropdownPartner({ row }: { row: Row<IPartner> }) {
  const partner = row.original;
  const deleteDialog = useDialog();
  const editDialog = useDialog();
  const closeRef = useRef<HTMLButtonElement>(null);
  const { deletePartner } = usePartner();
  const navigate = useNavigate();

  const handleCloseDialog = () => {
    closeRef.current?.click();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => navigate(`/partner/view/${partner.partner_id}`)}>
            <Eye size={16} />
            View Partner
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={editDialog.trigger}>
            <PenLine />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={deleteDialog.trigger}>
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog {...deleteDialog.props}>
        <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
          <DialogHeader>
            <DialogTitle>Confirm delete partner</DialogTitle>
            <DialogDescription className="py-4">
              are you sure you want to delete partner <span className="font-semibold">{partner.partner_id}</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              variant="default"
              type="button"
              onClick={() => {
                deletePartner(partner.partner_id as string);
              }}
            >
              Delete
            </Button>
            <DialogClose>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog {...editDialog.props}>
        <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
          <EditFormPartner partner={partner} onSuccess={handleCloseDialog} />
          <DialogClose ref={closeRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </>
  );
}

export const columns: ColumnDef<IPartner>[] = [
  {
    accessorKey: 'partner_number',
    header: 'Partner No.',
    cell: ({ row }) => <div>{row.getValue('partner_number')}</div>,
  },
  {
    accessorKey: 'name',
    header: () => <div>Name</div>,
    cell: ({ row }) => <div className="font-medium">{row.getValue('name')}</div>,
  },
  {
    accessorKey: 'email',
    header: () => <div>Email</div>,
    cell: ({ row }) => <div>{row.getValue('email')}</div>,
  },
  {
    accessorKey: 'phone',
    header: () => <div>Phone</div>,
    cell: ({ row }) => <div>{row.getValue('phone')}</div>,
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => <DropdownPartner row={row} />,
  },
];
